import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import * as api from '../api/inventory-counts';

export const useCounts = (params) =>
  useQuery({
    queryKey: ['inventory-counts', params],
    queryFn: () => api.getCounts(params),
  });

export const useCount = (uuid) =>
  useQuery({
    queryKey: ['inventory-counts', uuid],
    queryFn: () => api.getCount(uuid),
    enabled: !!uuid,
  });

export const useCountItems = (uuid, page, limit, search, filter) =>
  useQuery({
    queryKey: ['inventory-count-items', uuid, page, limit, search, filter],
    queryFn: () => api.getCountItems(uuid, page, limit, search, filter),
    enabled: !!uuid,
    placeholderData: keepPreviousData,
  });

export const useLookupCountItem = () =>
  useMutation({
    mutationFn: ({ countUuid, query }) => api.lookupCountItem(countUuid, query),
  });

export const useCreateCount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.createCount(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory-counts'] }),
  });
};

export const useStartCount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid) => api.startCount(uuid),
    onSuccess: (_, uuid) => {
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
      qc.invalidateQueries({ queryKey: ['inventory-counts', uuid] });
    },
  });
};

export const useUpdateCountItem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ countUuid, productUuid, countedQuantity, varianceReason, countedBy }) =>
      api.updateCountItem(countUuid, productUuid, {
        countedQuantity,
        varianceReason,
        countedBy,
      }),
    onSuccess: (_, { countUuid }) => {
      qc.invalidateQueries({ queryKey: ['inventory-count-items', countUuid] });
      qc.invalidateQueries({ queryKey: ['inventory-counts', countUuid] });
    },
  });
};

export const useCompleteCount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }) => api.completeCount(uuid, data),
    onSuccess: (_, { uuid }) => {
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
      qc.invalidateQueries({ queryKey: ['inventory-counts', uuid] });
      qc.invalidateQueries({ queryKey: ['inventory-count-items', uuid] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

export const useCancelCount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid) => api.cancelCount(uuid),
    onSuccess: (_, uuid) => {
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
      qc.invalidateQueries({ queryKey: ['inventory-counts', uuid] });
    },
  });
};

export const useDeleteCount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid) => api.deleteCount(uuid),
    onSuccess: (_, uuid) => {
      qc.removeQueries({ queryKey: ['inventory-counts', uuid] });
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
    },
  });
};
